import React, { useRef, useState } from "react";
import { Segmented } from "../../../../design-system";
import { CategoryTrend } from "../model/types";
import classes from "./StackedAreaChart.module.less";

const RANGES = ["6M", "YTD", "1Y", "ALL"] as const;
type Range = (typeof RANGES)[number];

/** Returns the starting index into `months` for the given range. Assumes
 * months without an apostrophe are in the current year (e.g., "Jan" vs
 * "Nov '25"). */
function rangeStartIndex(months: string[], range: Range): number {
    const n = months.length;
    switch (range) {
        case "6M":
            return Math.max(0, n - 6);
        case "YTD": {
            const i = months.findIndex((m) => !m.includes("'"));
            return i >= 0 ? i : 0;
        }
        case "1Y":
            return Math.max(0, n - 12);
        case "ALL":
        default:
            return 0;
    }
}

interface Props {
    months: string[];
    series: CategoryTrend[];
    /** Override the y-axis max (e.g. 100 for percent-stacked). When omitted,
     * the max is computed from the largest monthly column total. */
    yMax?: number;
    yTickFormatter?: (v: number) => string;
    valueFormatter?: (v: number) => string;
    height?: number;
}

const VIEW_W = 900;
const PAD_LEFT = 56;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 32;
const DEFAULT_HEIGHT = 260;
const BAR_WIDTH_RATIO = 0.6;

export const StackedAreaChart: React.FC<Props> = ({
    months: allMonths,
    series: allSeries,
    yMax,
    yTickFormatter = (v) => `${v}`,
    valueFormatter,
    height = DEFAULT_HEIGHT,
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const [range, setRange] = useState<Range>("1Y");
    const [hover, setHover] = useState<{
        index: number;
        x: number;
        y: number;
    } | null>(null);

    // Slice the source data to the selected range. Hover state is keyed by
    // the (possibly shifted) index, so we reset whenever range changes.
    const startIdx = rangeStartIndex(allMonths, range);
    const months = allMonths.slice(startIdx);
    const series = allSeries.map((s) => ({
        ...s,
        values: s.values.slice(startIdx),
    }));

    const plotW = VIEW_W - PAD_LEFT - PAD_RIGHT;
    const plotH = height - PAD_TOP - PAD_BOTTOM;

    const totals = months.map((_, i) =>
        series.reduce((sum, s) => sum + s.values[i], 0)
    );
    const computedMax = yMax ?? Math.ceil(Math.max(...totals) / 100) * 100;

    const slotW = plotW / months.length;
    const barW = slotW * BAR_WIDTH_RATIO;
    const slotCenter = (i: number) => PAD_LEFT + slotW * (i + 0.5);
    const yAt = (v: number) => PAD_TOP + plotH - (v / computedMax) * plotH;

    // Cumulative top of each band per month, bottom-up. cum[0] is baseline.
    const cum: number[][] = [new Array(months.length).fill(0)];
    for (const s of series) {
        const prev = cum[cum.length - 1];
        cum.push(prev.map((p, i) => p + s.values[i]));
    }

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => p * computedMax);
    const fmtValue = valueFormatter ?? yTickFormatter;

    const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = VIEW_W / rect.width;
        const localX = (e.clientX - rect.left) * ratio;
        let nearest = 0;
        let best = Infinity;
        for (let i = 0; i < months.length; i++) {
            const dist = Math.abs(slotCenter(i) - localX);
            if (dist < best) {
                best = dist;
                nearest = i;
            }
        }
        setHover({
            index: nearest,
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        });
    };

    const wrapWidth = wrapRef.current?.clientWidth ?? 0;
    const tooltipFlip = hover ? hover.x > wrapWidth / 2 : false;

    return (
        <div className={classes.wrap}>
            <div className={classes.topBar}>
                <Segmented
                    size="small"
                    value={range}
                    onChange={(v) => {
                        setRange(v as Range);
                        setHover(null);
                    }}
                    options={RANGES.map((r) => ({ label: r, value: r }))}
                />
            </div>
            <div className={classes.chartArea} ref={wrapRef}>
                <svg
                    viewBox={`0 0 ${VIEW_W} ${height}`}
                    style={{ width: "100%", height: "auto", display: "block" }}
                    onMouseMove={onMouseMove}
                    onMouseLeave={() => setHover(null)}
                >
                    {yTicks.map((v) => (
                        <g key={v}>
                            <line
                                x1={PAD_LEFT}
                                x2={VIEW_W - PAD_RIGHT}
                                y1={yAt(v)}
                                y2={yAt(v)}
                                stroke="#fafafa"
                                strokeWidth={1}
                            />
                            <text
                                x={PAD_LEFT - 8}
                                y={yAt(v) + 3}
                                textAnchor="end"
                                fontSize="7"
                                fill="#595959"
                            >
                                {yTickFormatter(v)}
                            </text>
                        </g>
                    ))}

                    {months.map((_, i) =>
                        series.map((s, k) => {
                            const bottomVal = cum[k][i];
                            const topVal = cum[k + 1][i];
                            if (topVal === bottomVal) return null;
                            const y = yAt(topVal);
                            const h = yAt(bottomVal) - y;
                            const x = slotCenter(i) - barW / 2;
                            return (
                                <rect
                                    key={`${i}-${s.name}`}
                                    x={x}
                                    y={y}
                                    width={barW}
                                    height={h}
                                    fill={s.color}
                                />
                            );
                        })
                    )}

                    {hover && (
                        <rect
                            x={slotCenter(hover.index) - barW / 2 - 4}
                            y={PAD_TOP}
                            width={barW + 8}
                            height={plotH}
                            fill="#262626"
                            fillOpacity={0.06}
                            pointerEvents="none"
                        />
                    )}

                    {months.map((m, i) => (
                        <text
                            key={m}
                            x={slotCenter(i)}
                            y={PAD_TOP + plotH + 18}
                            textAnchor="middle"
                            fontSize="7"
                            fill="#595959"
                        >
                            {m}
                        </text>
                    ))}
                </svg>

                {hover && (
                    <div
                        className={classes.tooltip}
                        style={{
                            left: tooltipFlip ? undefined : hover.x + 12,
                            right: tooltipFlip
                                ? wrapWidth - hover.x + 12
                                : undefined,
                            top: 8,
                        }}
                    >
                        <div className={classes.tooltipMonth}>
                            {months[hover.index]}
                        </div>
                        {[...series].reverse().map((s) => (
                            <div
                                key={s.name}
                                className={classes.tooltipRow}
                            >
                                <span
                                    className={classes.swatch}
                                    style={{ background: s.color }}
                                />
                                <span className={classes.tooltipName}>
                                    {s.name}
                                </span>
                                <span className={classes.tooltipValue}>
                                    {fmtValue(s.values[hover.index])}
                                </span>
                            </div>
                        ))}
                        <div className={classes.tooltipTotal}>
                            <span>Total</span>
                            <span>{fmtValue(totals[hover.index])}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className={classes.legend}>
                {series.map((s) => (
                    <div key={s.name} className={classes.legendItem}>
                        <span
                            className={classes.swatch}
                            style={{ background: s.color }}
                        />
                        <span>{s.name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
